
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const reviewSchema = z.object({
  content: z
    .string()
    .min(5, { message: "리뷰는 최소 5자 이상이어야 합니다." })
    .max(500, { message: "리뷰는 최대 500자까지 가능합니다." }),
});

interface ReviewFormProps {
  onSubmit: (rating: number, content: string) => void;
  isSubmitting?: boolean;
  existingReview?: any; // Using any type for simplicity, ideally should define a proper type
}

export const ReviewForm = ({ 
  onSubmit, 
  isSubmitting = false,
  existingReview = null 
}: ReviewFormProps) => {
  const [rating, setRating] = useState(5);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof reviewSchema>>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      content: existingReview?.content || "",
    },
  });

  // Update form when existingReview changes
  useEffect(() => {
    if (existingReview) {
      form.setValue("content", existingReview.content);
      setRating(existingReview.rating);
    }
  }, [existingReview, form]);

  const handleSubmit = (values: z.infer<typeof reviewSchema>) => {
    onSubmit(rating, values.content);
    // Don't reset the form after submission as we're now updating the review
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{existingReview ? "리뷰 수정" : "리뷰 작성"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="flex items-center space-x-1 mb-2">
              <FormLabel className="mr-2">평점:</FormLabel>
              {Array.from({ length: 5 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className="focus:outline-none"
                  onClick={() => setRating(i + 1)}
                >
                  <Star
                    className={`h-6 w-6 cursor-pointer ${
                      i < rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>내용</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="이용 경험을 자세히 작성해주세요..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "등록 중..." : existingReview ? "리뷰 수정하기" : "리뷰 등록하기"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

// Need to import Star from lucide-react
import { Star } from "lucide-react";
